<template>
  <div class="container page">
    <van-nav-bar :title="this.videoInfo.vod_name" class="nav-bar">
      <template #left>
        <van-icon name="arrow-left" color="#fff" @click="back()"/>
      </template>
    </van-nav-bar>
    <div class="movie-video">
      <video id="my-video"  class="video-js">
      </video>
    </div>
    <div class="movie-content">
      <div class="movie-descript">
        <p>{{ this.videoInfo.vod_name}}</p>
        <span>{{this.videoInfo.count}}{{ $t("video.num_play") }}</span>
      </div>

      <div class="movie-body">
        <div class="movie-title">
          <div>
            <span>{{ $t("index.recmonmand") }}</span>
          </div>
        </div>
        <div class="movie-list">
          <div class="movie-play-item" @click="toPlayVideo(v.id)" v-for="(v,key) in moreVideoInfo" :key="key">
            <div>
              <img :src="v.vod_pic">
              <div>
                <div class="van-count-down">{{ v.time }}</div>
              </div>
            </div>
            <div>
              <p>{{ v.vod_name }}</p>
              <span>{{ v.count }}{{ $t("video.num_play") }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import videojs from "video.js";
import "videojs-contrib-hls";

/* ====== HLS 片头广告跳过策略（2 层兜底） ======
 *
 * 【第 1 层：拦截 m3u8 文本，直接删广告片段】
 *   - 识别常见广告标记：#EXT-X-CUE-OUT / #EXT-X-CUE-IN / #EXT-X-SCTE35
 *     以及开头连续的 #EXT-X-DISCONTINUITY 广告段
 *   - 对带广告标记的：直接删除广告标记 + 广告 EXTINF 片段，保留正片
 *   - 对没有任何广告标记的片头：
 *       a. TS URI 命名像 preroll/ad 的丢弃
 *       b. 按「累计时长」丢弃：前几片累加时长 <= HEAD_AD_MAX_SECONDS（默认 30s）的全部丢
 *       c. 兜底按「片数」丢弃：SKIP_LEADING_SEGMENTS 片（默认 8 片，覆盖 25s 广告常见分片）
 *
 * 【第 2 层：播放器 loadedmetadata 后自动 seek】
 *   - 如果改 m3u8 失败（广告已经压进第一个 TS），自动跳过前 SKIP_HEAD_SECONDS 秒
 *   - 默认 27 秒（25s 广告 + 2s 余量，避免看到广告最后 1 秒）
 *
 * 所有参数集中在下面这个 HLS_SKIP_CFG 对象，后续可自由调。
 */
const HLS_SKIP_CFG = {
  // 第 1 层兜底：即使没有任何广告标记，也强制丢弃前 N 个 EXTINF 片（25s 广告 ≈ 8 片）
  SKIP_LEADING_SEGMENTS: 8,
  // 第 1 层「累计时长阈值」：前几片 EXTINF 累计时间 <= HEAD_AD_MAX_SECONDS 秒的都判为片头广告整块丢
  HEAD_AD_MAX_SECONDS: 30,
  // 第 2 层（兜底）：loadedmetadata 后自动 seek 跳过前多少秒（>= 0 生效）
  SKIP_HEAD_SECONDS: 27,
  // 匹配广告标记（正则，按行匹配）
  AD_TAG_REGEX: /#EXT-X-(CUE-OUT|SCTE35|AD|DATERANGE.*CLASS="com\.apple\.hls\.splice|SPLICE-INSERT|SCTE35-OUT)/i,
  AD_END_REGEX: /#EXT-X-(CUE-IN|SCTE35-IN|SCTE-IN|AD-END)/i,
  // 匹配广告 TS URL（文件命名规律）
  AD_SEGMENT_REGEX: /(^|[^a-z])(ad|ads|preroll|pre[_-]?roll|commercial|spot)[_-]?\d*\.(ts|m4s|aac|mp4)/i,
};

// 单行广告标记（CUE-OUT 往往会带 DURATION=15.000，把这整行去掉）
function isAdTagLine(line) {
  return HLS_SKIP_CFG.AD_TAG_REGEX.test(line);
}
function isAdEndTagLine(line) {
  return HLS_SKIP_CFG.AD_END_REGEX.test(line);
}
function isAdSegmentUri(line) {
  return HLS_SKIP_CFG.AD_SEGMENT_REGEX.test(line);
}
// 从 #EXTINF:4.992, 这样的行里把秒数提取为 float，失败返回 null
function parseExtInfDuration(line) {
  try {
    const m = line.match(/#EXTINF\s*:\s*([0-9]+(?:\.[0-9]+)?)\s*,?/i);
    if (!m) return null;
    const n = parseFloat(m[1]);
    return isNaN(n) ? null : n;
  } catch (e) { void e; return null; }
}

/**
 * 核心：将原始 m3u8 文本中的广告片段/标记过滤掉，返回干净的正片 m3u8
 * 保留：#EXTM3U / EXT-X-VERSION / TARGETDURATION / MEDIA-SEQUENCE / PLAYLIST-TYPE / MAP / KEY 等头部
 * 删除：开头广告 EXTINF 段 + 广告标记行
 */
function sanitizeHlsPlaylist(rawText) {
  if (typeof rawText !== 'string' || rawText.length < 10) return rawText;
  const lines = rawText.split(/\r?\n/);
  // 丢弃前 N 片兜底参数
  const forceDropLeading = HLS_SKIP_CFG.SKIP_LEADING_SEGMENTS || 0;
  // 累计时长阈值：前几片加起来 <= 30s 的都整体当片头广告丢
  const maxAdHeadSec = HLS_SKIP_CFG.HEAD_AD_MAX_SECONDS || 30;

  const out = [];
  let i = 0;
  // 先把 M3U 头部全局行（出现在第一个 EXTINF 之前）都保留
  while (i < lines.length) {
    const line = lines[i];
    const trimmed = line.trim();
    if (trimmed === '') { i++; continue; }
    if (trimmed.indexOf('#EXTINF') === 0) break;
    // 头部的广告标记（如 #EXT-X-SCTE35 带 SCTE 二进制）直接丢
    if (isAdTagLine(trimmed)) { i++; continue; }
    out.push(line);
    i++;
  }

  let inAdBlock = false;      // 当前是否在 CUE-OUT ~ CUE-IN 之间的广告段
  let mainStarted = false;    // 是否已进入正片
  let segIndex = 0;           // 已经处理到的 EXTINF 序号
  let accumHeadSeconds = 0;   // 片头累计广告时长（秒，float）
  let pendingExtInf = null;   // 当前等待匹配 TS URI 的 #EXTINF 行: {raw, duration}

  while (i < lines.length) {
    const line = lines[i];
    const trimmed = line.trim();
    if (trimmed === '') { i++; continue; }

    // ===== 先处理广告标记 =====
    if (isAdTagLine(trimmed)) {
      inAdBlock = true;
      pendingExtInf = null;
      i++;
      continue;
    }
    if (isAdEndTagLine(trimmed)) {
      inAdBlock = false;
      pendingExtInf = null;
      // CUE-IN 结束后紧跟着一行 DISCONTINUITY（从广告切回正片）一般也可丢
      if (i + 1 < lines.length && lines[i + 1].trim().indexOf('#EXT-X-DISCONTINUITY') === 0) {
        i++;
      }
      i++;
      continue;
    }
    // 单独的 DISCONTINUITY：如果还没进入正片（inAdBlock 或广告刚结束后面那行）丢
    if (trimmed.indexOf('#EXT-X-DISCONTINUITY') === 0) {
      if (!mainStarted) { pendingExtInf = null; i++; continue; }
      out.push(line);
      i++;
      continue;
    }
    // EXTINF：暂存起来，等下一行 TS URI 到了再一起判断是不是片头广告段
    if (trimmed.indexOf('#EXTINF') === 0) {
      const d = parseExtInfDuration(trimmed);
      pendingExtInf = { raw: line, duration: d };
      i++;
      continue;
    }
    // 其他所有 #EXT-X-* 行都保留
    if (trimmed.charAt(0) === '#') {
      out.push(line);
      i++;
      continue;
    }

    // ===== 走到这里一定是 TS URI（资源行） =====

    if (inAdBlock) {
      // 当前处于 CUE-OUT 广告块 → 丢弃 URI + 前面没 push 的 EXTINF
      pendingExtInf = null;
      i++;
      continue;
    }
    if (isAdSegmentUri(trimmed)) {
      // 命名就像 preroll/ad 的 URI 直接丢
      pendingExtInf = null;
      i++;
      continue;
    }

    // ===== 片头累计时长判断（最关键，精准识别 25s 广告块） =====
    if (!mainStarted && pendingExtInf && pendingExtInf.duration != null && maxAdHeadSec > 0) {
      const will = accumHeadSeconds + pendingExtInf.duration;
      if (will <= maxAdHeadSec + 0.5) {      // 0.5s 浮点误差兜底
        // 累计还没超 30s → 这一片继续当广告整体丢掉
        accumHeadSeconds = will;
        pendingExtInf = null;
        segIndex++;
        i++;
        continue;
      }
      // 一旦累计超了，就把这片 EXTINF 留下进入正片流程
    }
    // 兜底：按片数丢前 8 片（极端情况累计时长判断没命中）
    if (!mainStarted && forceDropLeading > 0 && segIndex < forceDropLeading) {
      pendingExtInf = null;
      segIndex++;
      i++;
      continue;
    }

    // ===== 真正进入正片 =====
    if (pendingExtInf) { out.push(pendingExtInf.raw); pendingExtInf = null; }
    mainStarted = true;
    segIndex++;
    out.push(line);
    i++;
  }

  // 如果直到文件尾还有未匹配的 EXTINF（极少见），不要残留到 out 里
  // （这里 pendingExtInf 没 push 就自然丢了）

  // 尾部：#EXT-X-ENDLIST 保留（如果原来有）
  if (rawText.indexOf('#EXT-X-ENDLIST') > -1 && !out.some(l => l.trim() === '#EXT-X-ENDLIST')) {
    out.push('#EXT-X-ENDLIST');
  }

  let result = out.join('\n');
  // 防止处理后文件空了（极端情况）直接退回原文
  if (result.length < 30) return rawText;
  // 为了避免「过滤广告后，MEDIA-SEQUENCE 仍然是原包含广告的序号」导致 HLS
  // 播第一片 seek 到不存在位置，把 MEDIA-SEQUENCE 重置为 0 更稳
  result = result.replace(
    /(#EXT-X-MEDIA-SEQUENCE):\s*\d+/i,
    '$1:0'
  );
  // 如果原 m3u8 里带广告且 DISCONTINUITY 连续多个，清理一下（否则 Safari 偶发卡）
  result = result.replace(/(\s*#EXT-X-DISCONTINUITY\s*\n){2,}/g, '\n#EXT-X-DISCONTINUITY\n');
  return result;
}

/**
 * 更稳妥的方案：只 patch videojs 内部的 xhr 函数（video.js 的 VHS/HLS 请求都走它）
 * 绝不污染 axios / fetch / XMLHttpRequest 原型，其他 API 完全不受影响
 * 返回 { install, uninstall }，在组件 mounted/destroyed 调用。
 */
function installVideoJsM3u8Filter() {
  const ctx = { installed: false, origXhr: null };
  function isM3u8Url(url) {
    return typeof url === 'string' && /\.m3u8(\?|#|$)/i.test(url);
  }
  function install() {
    if (ctx.installed) return;
    if (typeof videojs === 'undefined' || !videojs.xhr) return;
    ctx.origXhr = videojs.xhr;
    // videojs.xhr(options, callback)  or  videojs.xhr(url, options, callback)
    videojs.xhr = function patchedVideoJsXhr(...args) {
      let opts = args[0];
      let cb = args[args.length - 1];
      let url = '';
      if (typeof opts === 'string') {
        url = opts;
      } else if (opts && typeof opts === 'object') {
        url = opts.uri || opts.url || '';
      }
      const needFilter = isM3u8Url(url);
      const wrappedCb = function (err, response, body) {
        if (!needFilter || err || !body) {
          return cb(err, response, body);
        }
        // body 一般是 string（m3u8 文本），但有时是 Uint8Array/Buffer
        let txt = body;
        if (typeof body !== 'string') {
          try {
            if (typeof TextDecoder !== 'undefined') {
              txt = new TextDecoder('utf-8').decode(body);
            } else {
              txt = String.fromCharCode.apply(null, new Uint8Array(body));
            }
          } catch (e) {
            // 解码失败则直接走原 callback
            void e;
            return cb(err, response, body);
          }
        }
        const clean = sanitizeHlsPlaylist(txt);
        return cb(err, response, clean);
      };
      // 保证最后一个参数是 callback
      const newArgs = args.slice(0, -1).concat([wrappedCb]);
      return ctx.origXhr.apply(videojs, newArgs);
    };
    ctx.installed = true;
  }
  function uninstall() {
    if (!ctx.installed || !ctx.origXhr) return;
    videojs.xhr = ctx.origXhr;
    ctx.origXhr = null;
    ctx.installed = false;
  }
  return { install, uninstall };
}

export default {
  data() {
    return {
      nowPlayVideoUrl: "",
      cover:"",
      userInfo:[],
      videoInfo:{},
      moreVideoInfo:{},
      player:null,
      is_play:false,
      times: null,
      is_see:0,
      hlsInterceptor: null,    // videojs m3u8 过滤器（install/uninstall）
      skippedOnce: false,      // 第二层：每个视频只自动跳过一次
    };
  },
  methods: {
    back(){
      this.$router.push({path:'Home'})
    },
    getVideoInfo(){

      this.$http({
        method: 'get',
        data:{id:this.$route.query.id},
        url: 'video_get_info'
      }).then(res=>{
        this.videoInfo = res.data;
        this.nowPlayVideoUrl = this.videoInfo.vod_play_url;
        this.cover = this.videoInfo.vod_pic;
        let videos = document.getElementById('my-video');
        videos.poster = this.cover;
        this.skippedOnce = false; // 换新视频重置跳过标记
        this.getVideo();
      })

    },
    toPlayVideo(id){
      if(!localStorage.getItem('token')){
        this.$router.push({path:'/Login'})
      }else {
        this.$router.push({path:'?id='+id})
        location.reload();
      }

    },
    getMoreVideoItem(){
      this.$http({
        method: 'get',
        url: 'video_get_more_item'
      }).then(res=>{
          this.moreVideoInfo = res.data;
      })
    },
    getVideo() {
      // this.player = videojs("my-video",  {
      //   height:"200px",
      //   preload: "auto", // 预加载
      //   controls: true,  // 显示播放的控件
      //   multipleArray: [0.75, 1, 1.5, 2], // 倍速设置
      // });
      this.player.src([{
        src: this.nowPlayVideoUrl,
        type: "application/x-mpegURL" // 告诉videojs,这是一个hls流
      }]);
    },
    getUserInfo(){
      this.$http({
        method: 'get',
        url: 'user_info'
      }).then(res=>{
        if(res.code === 200){
          this.userInfo = res.data;
          this.is_see = this.userInfo.is_see;
          if(this.userInfo.status !== 1){
            this.$toast(this.$t("video.account_out"));
            localStorage.clear()
            this.$router.push({path:'/Login'})
          }else {
            if(this.$store.getters.getBaseInfo.isplay == 1){
              this.getVideoInfo();
              this.getMoreVideoItem()
              // if(this.userInfo.money <= "0.00"){
              //   this.$toast(this.$t("video.buy"));
              //   this.$router.push({path:'/Home'})
              // }
            }else {
              this.getVideoInfo();
              this.getMoreVideoItem();
            }
          }
        }else if(res.code ===401){
          this.$toast(res.msg);
        }
      })
    },
  },
  mounted(){
    const _this = this;
    // 第 1 层：在 videojs 实例创建前装好 m3u8 过滤器（只 patch videojs.xhr，不碰其他全局请求）
    this.hlsInterceptor = installVideoJsM3u8Filter();
    this.hlsInterceptor.install();

    if(!localStorage.getItem('token')){
      this.$router.push({path:'/Login'})
    }else {
      this.player = videojs("my-video",  {
        height:"200px",
        preload: "auto", // 预加载
        controls: true,  // 显示播放的控件
        multipleArray: [0.75, 1, 1.5, 2], // 倍速设置
      },function(){
        this.on("play",() => {
          _this.is_play=true;
        });
        // 第 2 层（兜底）：元数据加载完成后，如果当前还在 0s 左右且
        // SKIP_HEAD_SECONDS>0，自动 seek 跳过片头
        this.on("loadedmetadata", function () {
          if (_this.skippedOnce) return;
          const skipSec = HLS_SKIP_CFG.SKIP_HEAD_SECONDS;
          try {
            const ct = this.currentTime() || 0;
            const dur = this.duration() || 0;
            // 跳过条件：配置>0；时长合理（> skipSec*2）；当前在片头（<= 1s）
            if (skipSec > 0 && dur > skipSec * 2 && ct <= 1) {
              this.currentTime(skipSec);
              _this.skippedOnce = true;
              try { _this.$toast && _this.$toast(_this.$t('video.skip_ad')); } catch (e) { /* toast 初始化失败不影响播放 */ }
              
              // === 修复：解决 seek 后播放按钮不显示的问题 ===
              // 强制暂停，确保显示播放按钮
              this.pause();
              
              // 监听一次性的 seeked 事件，在跳转完成后恢复 UI 状态
              this.once('seeked', function() {
                // 确保大播放按钮可见
                if (this.bigPlayButton) {
                  this.bigPlayButton.show();
                }
                // 强制用户交互状态，避免控件被误隐藏
                this.userActive(true);
              });
              // ============================================
            }
          } catch (e) { // 某些浏览器无法立刻 seek，不中断流程
            void e;
          }
        });
      });
      this.getUserInfo();
      this.times = setInterval(() => {
        if(this.is_play && this.is_see == 0){
          const ct = Math.round(this.player.currentTime())
          if(ct >= 180){
            this.player.pause()
            this.$toast(this.$t("video.buy"));
            return
          }
        }
      }, 1000 * 2);
    }

  },

  destroyed () {
    if(this.is_play){
      this.is_play = false;
    }
    clearInterval(this.times);
    // 卸载 m3u8 拦截，避免污染其他页面
    if (this.hlsInterceptor) {
      this.hlsInterceptor.uninstall();
      this.hlsInterceptor = null;
    }
  }
};
</script>

<style scoped>
.video-js {
  width: 100%;
  /*height: 420px;*/
  font-size: 24px;
}
.movie-content{
  flex: 1;
  overflow-y: auto;
}
.movie-content .movie-descript{
  width: 100%;
  height: 140px;
  background-color: #fff;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: space-between;
  padding: 15px;
}
.movie-content .movie-descript p{
  font-size: 30px;
  font-weight: 700;
  color: #000;
}
.movie-content .movie-descript span{
  color: #979799;
}
.movie-content .movie-body{
  width: calc(100% - 20px);
  margin: 0 auto;
}
::v-deep .movie-video .video-js .vjs-big-play-button {
  top: 50%;
  left: 50%;
  margin-top: -50px;
  margin-left: -100px;
}
.movie-content .movie-body .movie-title{
  height: 70px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.movie-content .movie-body .movie-title>div:first-child {
  width: 410px;
}
.movie-content .movie-body .movie-title>div:first-child span{
  display: flex;
  align-items: center;
  justify-content: flex-start;
  font-size: 30px;
  font-weight: 700;
  color: #000;
}
.movie-content .movie-body .movie-title>div:first-child span:before {
  content: "";
  display: block;
  width: 8px;
  height: 30px;
  background-color: #f487e0;
  border-radius: 25px;
  margin-right: 10px;
}
.movie-play-item{
  width: 100%;
  height: 200px;
  border-radius: 10px;
  position: relative;
  display: flex;
  background-color: #fff;
  margin-bottom: 20px;
}
.movie-play-item>div{
  height: 100%;
}
.movie-play-item>div:first-child {
  width: 200px;
  position: relative;
}
.movie-play-item>div:first-child>img{
  width: 100%;
  height: 100%;
  border-radius: 10px 0 0 10px;
}
.movie-play-item>div:first-child>div{
  position: absolute;
  width: 100%;
  height: 30px;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 10px;
  background-color: rgba(0,0,0,.4);
  border-radius: 0 0 0 10px;
}
.movie-play-item>div:first-child>div .van-count-down {
  color: #fff;
  font-size: 25px;
}
.movie-play-item>div:nth-child(2) p{
  width: 500px;
  height: 60px;
  font-size: 30px;
  line-height: 32px;
  word-break: break-all;
  overflow: hidden;
  color: #000;
}
.movie-play-item>div:nth-child(2) span{
  color: #000;
}
.movie-play-item>div:nth-child(2) {
  padding: 20px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: space-between;
}
abbr, address, article, aside, audio, b, blockquote, body, canvas, caption, cite, code, dd, del, details, dfn, div, dl, dt, em, fieldset, figcaption, figure, footer, form, h1, h2, h3, h4, h5, h6, header, hgroup, html, i, iframe, img, ins, kbd, label, legend, li, mark, menu, nav, object, ol, p, pre, q, samp, section, small, span, strong, sub, summary, sup, table, tbody, td, tfoot, th, thead, time, tr, ul, var, video {
  margin: 0;
  padding: 0;
  border: 0;
  outline: 0;
  font-size: 100%;
  vertical-align: baseline;
  box-sizing: border-box;
}

.vjs-big-play-button .vjs-icon-placeholder {
  font-size: 1.63em !important;
}
</style>